import React, {FC, Profiler, PropsWithChildren} from 'react';

import {
  annotateTaskTiming,
  TaskTimingTypesEnum,
} from '../../../libs/shared/core/src/helpers/instrumentation/eventLoopPerformanceHelpers';
import {
  RenderProfilerProps,
  RenderTypesEnum,
} from '../../../libs/shared/ui/src/components/performanceMetrics/renderProfilerContext';
import {useAppSelector} from '../../../src/helpers/appReduxHelpers';
import {getHasPerformanceLogging} from './performanceMetricSelectors';

/*
 * Props.
 */

/*
 * Component.
 */

export const RenderProfiler: FC<PropsWithChildren<RenderProfilerProps>> = (props) => {
  const hasPerformanceLogging = useAppSelector(getHasPerformanceLogging);
  if (!hasPerformanceLogging) {
    return <>{props.children}</>;
  }

  return (
    <Profiler
      id={props.type}
      onRender={(_id, phase, actualDuration, baseDuration, startTime, commitTime) =>
        renderProfilerCallback(props.type, phase, actualDuration, baseDuration, startTime, commitTime)
      }
    >
      {props.children}
    </Profiler>
  );
};

/*
 * Helpers.
 */

function renderProfilerCallback(
  renderType: RenderTypesEnum,
  _phase: 'mount' | 'update' | 'nested-update',
  actualDuration: number,
  _baseDuration: number,
  startTime: number,
  _commitTime: number,
) {
  // All of the other renders are a subset of the app render.
  const isOverlappingRender = renderType !== RenderTypesEnum.APP;

  // For reporting, consider the end time to be the start time plus the duration of the render.
  // In reality, the updated UI isn't shown on screen until commitTime, but the duration between
  // startTime and commitTime includes everything that rendered including things that are not in
  // this profiler tree. Suppose in one task, we render cells and viewer. The app render, cells
  // render, and viewer render time might be [0, 100), [0, 40), [40, 100) respectively, but we'll
  // report app render as [0, 100), cells render as [0, 40), and viewer render as [0, 60). While
  // this is not ideal, it still gives us some insight into how long the render took for each part
  // of the tree, even though the offsets of when they occurred might be wrong.
  annotateTaskTiming(TaskTimingTypesEnum.RENDER, startTime, actualDuration, renderType, isOverlappingRender);
}
